// The assistant (ERP-129): a conversation in the top bar's drawer or on its own page.
//
// The server runs the model and answers what it can itself (the API, read as you). Two things only
// the browser has, it asks the browser for: the help articles, and the router to open a page. A
// change waits here, as a card, for you to confirm or decline it.
//
// The answer shows while it is written (ERP-135), files go with a message (ERP-134), and the
// record on screen is offered as what "this" means.
import {
  api,
  enabledPlugins,
  getBlob,
  loadHelp,
  locale,
  post,
  postForm,
  searchHelp,
  t,
  useAuth,
  useToast,
} from "@keel/core";
import { Field, Icon, useCurrentRecord } from "@keel/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, type KeyboardEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Answer } from "./Answer";

interface Block {
  type: "text" | "tool_use" | "tool_result" | "file" | "other";
  text?: string;
  mediaType?: string;
  id?: string;
  name?: string;
  // biome-ignore lint/suspicious/noExplicitAny: the model's tool input is free-form JSON
  input?: any;
  isError?: boolean;
}
interface Message {
  id: string;
  role: "user" | "assistant";
  content: Block[];
  createdAt: string;
}
interface Step {
  id: string;
  name: string;
  // biome-ignore lint/suspicious/noExplicitAny: the model's tool input is free-form JSON
  input: any;
  kind: "server" | "confirm" | "client";
}
interface Conversation {
  id: string;
  title: string;
  status: "idle" | "thinking" | "waiting" | "failed";
  error: string | null;
  pending: Step[];
  messages: Message[];
  draft: string | null;
}
interface Upload {
  id: string;
  name: string;
  mediaType: string;
  size: number;
}
interface Status {
  configured: boolean;
  model: string | null;
  source: "personal" | "company" | "server" | null;
  personalKeys: boolean;
  canManage: boolean;
}
interface MyKey {
  hasKey: boolean;
  keyHint: string | null;
  allowed: boolean;
  source: Status["source"];
  warning: string | null;
}
interface Brief {
  id: string;
  title: string;
  status: string;
  updatedAt: string;
}

/** Closes the drawer the chat sits in, for links that lead away from it; nothing on the page. */
export const CloseAssistant = createContext<() => void>(() => {});

const KEY = "keel-assistant-conversation";
const remembered = () => {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
};
const remember = (id: string | null) => {
  try {
    if (id) sessionStorage.setItem(KEY, id);
    else sessionStorage.removeItem(KEY);
  } catch {
    /* private mode */
  }
};

const EXAMPLES = [
  "What do I need to do today?",
  "Which loans are overdue, and by how much?",
  "How do I end a loan early?",
  "Make a task for me to call back the last customer I added",
];

/** Where you are and what your menu offers, so "this" and "open the list" mean something. */
function usePlace() {
  const { me, can, company } = useAuth();
  const { pathname } = useLocation();
  const record = useCurrentRecord();
  return () => ({
    record: record ? { entityType: record.entityType, entityId: record.entityId, label: record.label } : null,
    page: pathname,
    pageTitle: document.title,
    company: company.name,
    language: locale,
    pages: enabledPlugins(me.modules)
      .flatMap((p) => p.nav ?? [])
      .flatMap((g) => g.items.map((i) => ({ ...i, group: g.group })))
      .filter((i) => !i.operator && (!i.perm || can(i.perm)))
      .map((i) => ({ label: `${i.group} › ${i.label}`, path: i.to })),
  });
}

/** One line for a step the assistant took, as you'd say it. */
function described(b: Block): string {
  const i = b.input ?? {};
  switch (b.name) {
    case "search_help":
      return `${t("Read the help on")} “${i.query ?? ""}”`;
    case "open_page":
      return `${t("Opened")} ${i.path ?? ""}`;
    case "find_operations":
      return `${t("Looked for how to")} “${i.query ?? ""}”`;
    case "describe_operation":
      return `${t("Checked")} ${i.method ?? ""} ${i.path ?? ""}`;
    case "read_attachment":
      return t("Read an attached file");
    case "call_api":
      return String(i.method ?? "GET").toUpperCase() === "GET"
        ? `${t("Looked up")} ${i.path ?? ""}`
        : `${t("Asked you")}: ${i.summary ?? `${i.method} ${i.path}`}`;
    default:
      return b.name ?? "";
  }
}

/** Open a file given to the assistant, with the session's sign-in. */
async function openFile(id: string, name: string) {
  const blob = await getBlob(`/assistant/files/${id}`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.gif,.xlsx,.xls,.ods,.csv,.txt,.md";

export function Chat({
  full = false,
  prefill = null,
}: {
  full?: boolean;
  /** A question to put in the box, from `askAssistant` (ERP-135). */
  prefill?: { text: string; n: number } | null;
}) {
  const { me } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const context = usePlace();
  const [id, setId] = useState<string | null>(remembered);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(false);
  const [keys, setKeys] = useState(false);
  const settling = useRef(new Set<string>());
  const end = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [uploading, setUploading] = useState(0);
  const [dropping, setDropping] = useState(false);
  const record = useCurrentRecord();

  // A question handed over from elsewhere on the page goes in the box, to send or change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per hand-over
  useEffect(() => {
    if (!prefill?.text) return;
    setDraft(prefill.text);
    setHistory(false);
    setKeys(false);
    setTimeout(() => box.current?.focus(), 0);
  }, [prefill?.n]);

  // The box grows with what is typed, up to a point.
  // biome-ignore lint/correctness/useExhaustiveDependencies: measured on every change of the text
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
  }, [draft]);

  const attach = async (files: FileList | File[]) => {
    const list = Array.from(files).slice(0, Math.max(0, 5 - uploads.length));
    if (files.length > list.length) toast(t("At most 5 files with one message"), "error");
    for (const f of list) {
      setUploading((n) => n + 1);
      try {
        const form = new FormData();
        form.append("file", f, f.name);
        const u = await postForm<Upload>("/assistant/files", form);
        setUploads((x) => [...x, u]);
      } catch (e) {
        toast(`${f.name}: ${(e as Error).message}`, "error");
      } finally {
        setUploading((n) => n - 1);
      }
    }
  };

  const status = useQuery({
    queryKey: ["assistant-status"],
    queryFn: () => api<Status>("/assistant/status"),
    staleTime: 60_000,
  });
  const conversation = useQuery({
    queryKey: ["assistant", id],
    queryFn: () => api<Conversation>(`/assistant/conversations/${id}`),
    enabled: !!id,
    retry: false,
    // Often while the answer is written, so it reads as it comes.
    refetchInterval: (q) => (q.state.data?.status === "thinking" ? 400 : false),
  });
  const list = useQuery({
    queryKey: ["assistant-list"],
    queryFn: () => api<Brief[]>("/assistant/conversations"),
    enabled: history,
  });
  const c = conversation.data;

  // A conversation that is gone (deleted elsewhere) starts afresh.
  useEffect(() => {
    if (conversation.isError) {
      setId(null);
      remember(null);
    }
  }, [conversation.isError]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on each new message or status
  useEffect(() => {
    end.current?.scrollIntoView({ block: "end" });
  }, [c?.messages.length, c?.status, c?.pending.length, c?.draft]);

  const show = (next: Conversation) => qc.setQueryData(["assistant", next.id], next);

  const settle = async (step: Step, answer: { approve?: boolean; result?: string; isError?: boolean }) => {
    if (!c) return;
    const next = await post<Conversation>(`/assistant/conversations/${c.id}/steps/${step.id}`, answer);
    show(next);
  };

  // What only the browser can answer: the help articles, and opening a page.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the waiting steps change
  useEffect(() => {
    if (c?.status !== "waiting") return;
    for (const step of c.pending.filter((s) => s.kind === "client")) {
      if (settling.current.has(step.id)) continue;
      settling.current.add(step.id);
      (async () => {
        try {
          if (step.name === "search_help") {
            const sections = await loadHelp(me.modules);
            const hits = searchHelp(sections, String(step.input?.query ?? "")).slice(0, 3);
            const result = hits.length
              ? hits
                  .map(
                    (h) =>
                      `# ${h.article.title}\nAddress: /help/${h.article.module}/${h.article.slug}\n\n${h.article.body.slice(0, 6000)}`,
                  )
                  .join("\n\n---\n\n")
              : "No help article matches those words. Try fewer or other words.";
            await settle(step, { result });
          } else if (step.name === "open_page") {
            const path = String(step.input?.path ?? "");
            if (path.startsWith("/") && !path.startsWith("//")) {
              navigate(path);
              await settle(step, { result: `Opened ${path}.` });
            } else await settle(step, { result: "Only pages of the app can be opened.", isError: true });
          } else await settle(step, { result: `The browser can't do ${step.name}.`, isError: true });
        } catch (e) {
          settling.current.delete(step.id);
          toast((e as Error).message, "error");
        }
      })();
    }
  }, [c?.pending, c?.status]);

  const send = async (text: string) => {
    const message = text.trim() || (uploads.length ? t("Read the attached files.") : "");
    if (!message || busy || uploading) return;
    setBusy(true);
    try {
      const body = { message, context: context(), files: uploads.map((u) => u.id) };
      const next = id
        ? await post<Conversation>(`/assistant/conversations/${id}/messages`, body)
        : await post<Conversation>("/assistant/conversations", body);
      setId(next.id);
      remember(next.id);
      show(next);
      setDraft("");
      setUploads([]);
      qc.invalidateQueries({ queryKey: ["assistant-list"] });
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(false);
    }
  };

  const fresh = () => {
    setId(null);
    remember(null);
    setHistory(false);
    setKeys(false);
    setDraft("");
  };

  const remove = async (cid: string) => {
    await api(`/assistant/conversations/${cid}`, { method: "DELETE" });
    if (cid === id) fresh();
    qc.invalidateQueries({ queryKey: ["assistant-list"] });
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send(draft);
    }
  };

  const thinking = c?.status === "thinking";
  const confirms = (c?.status === "waiting" ? c.pending : []).filter((s) => s.kind === "confirm");
  const shown = useMemo(
    () =>
      (c?.messages ?? [])
        .map((m) => ({
          ...m,
          content: m.content.filter((b) => b.type === "text" || b.type === "tool_use" || b.type === "file"),
        }))
        .filter((m) => m.content.length > 0),
    [c?.messages],
  );

  const lastBot = [...shown].reverse().find((m) => m.role === "assistant")?.id;
  const idle = c?.status === "idle";

  // No key anywhere yet: say how to get one, and let the person add their own when allowed.
  if (status.data && !status.data.configured)
    return (
      <div className={`assistant ${full ? "assistant-full" : ""}`}>
        <div className="assistant-scroll">
          <div className="assistant-empty">
            <Icon name="sparkle" size={22} />
            <p>{t("The assistant has no API key yet.")}</p>
          </div>
          <KeyPanel status={status.data} />
        </div>
      </div>
    );

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: a drop target for files; the attach button is the keyboard way
    <div
      className={`assistant ${full ? "assistant-full" : ""} ${dropping ? "dropping" : ""}`}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes("Files")) {
          e.preventDefault();
          setDropping(true);
        }
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setDropping(false);
      }}
      onDrop={(e) => {
        if (!e.dataTransfer.files.length) return;
        e.preventDefault();
        setDropping(false);
        attach(e.dataTransfer.files);
      }}
    >
      <div className="assistant-bar">
        <button type="button" className="btn btn-sm btn-ghost" onClick={fresh} disabled={!id}>
          <Icon name="plus" size={14} />
          {t("New conversation")}
        </button>
        <button
          type="button"
          className={`btn btn-sm btn-ghost ${history ? "on" : ""}`}
          onClick={() => {
            setHistory((h) => !h);
            setKeys(false);
          }}
          title={t("Your earlier conversations. Nobody else sees them.")}
        >
          <Icon name="history" size={14} />
          {t("Earlier")}
        </button>
        <button
          type="button"
          className={`btn btn-sm btn-ghost ${keys ? "on" : ""}`}
          onClick={() => {
            setKeys((k) => !k);
            setHistory(false);
          }}
          title={t("Whose API key the assistant uses for you, and your own.")}
        >
          <Icon name="key" size={14} />
          {t("Key")}
        </button>
      </div>

      {keys && status.data ? (
        <div className="assistant-scroll">
          <KeyPanel status={status.data} />
        </div>
      ) : history ? (
        <div className="assistant-scroll">
          {(list.data ?? []).length === 0 ? (
            <p className="muted">{list.isLoading ? t("Loading…") : t("No conversations yet.")}</p>
          ) : (
            <ul className="assistant-history">
              {(list.data ?? []).map((x) => (
                <li key={x.id} className={x.id === id ? "on" : ""}>
                  <button
                    type="button"
                    className="assistant-history-open"
                    onClick={() => {
                      setId(x.id);
                      remember(x.id);
                      setHistory(false);
                    }}
                  >
                    <span>{x.title || t("Untitled")}</span>
                    <small className="muted">{new Date(x.updatedAt).toLocaleString()}</small>
                  </button>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={t("Delete conversation")}
                    title={t("Delete conversation")}
                    onClick={() => remove(x.id)}
                  >
                    <Icon name="trash" size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="assistant-scroll" aria-live="polite">
          {!c && (
            <div className="assistant-empty">
              <Icon name="sparkle" size={22} />
              <p>
                {t(
                  "Ask how something works, ask about your data, or ask me to do something. I work with your own rights and ask before I change anything.",
                )}
              </p>
              <div className="assistant-examples">
                {EXAMPLES.map((x) => (
                  <button key={x} type="button" className="chip" onClick={() => send(t(x))}>
                    {t(x)}
                  </button>
                ))}
              </div>
            </div>
          )}
          {shown.map((m) =>
            m.role === "user" ? (
              <div key={m.id} className="assistant-msg user">
                {m.content.map((b, i) =>
                  b.type === "file" ? (
                    <button
                      key={b.id}
                      type="button"
                      className="assistant-file"
                      onClick={() =>
                        openFile(b.id ?? "", b.name ?? "file").catch((e) => toast(e.message, "error"))
                      }
                      title={t("Open the file")}
                    >
                      <Icon name="paperclip" size={12} />
                      {b.name}
                    </button>
                  ) : (
                    <p key={i}>{b.text}</p>
                  ),
                )}
              </div>
            ) : (
              <div key={m.id} className="assistant-msg bot">
                {m.content.map((b, i) =>
                  b.type === "text" ? (
                    <Answer
                      key={i}
                      text={b.text ?? ""}
                      last={idle && m.id === lastBot}
                      onAsk={(q) => send(q)}
                    />
                  ) : (
                    <div key={b.id} className="assistant-step muted">
                      <Icon name="check" size={12} />
                      {described(b)}
                    </div>
                  ),
                )}
              </div>
            ),
          )}
          {confirms.map((s) => (
            <ConfirmCard key={s.id} step={s} onAnswer={(approve) => settle(s, { approve })} />
          ))}
          {thinking && c?.draft && (
            <div className="assistant-msg bot assistant-draft">
              <Answer text={c.draft} last={false} onAsk={() => {}} />
            </div>
          )}
          {thinking && (
            <div className="assistant-msg bot assistant-thinking muted">
              <span className="dot" />
              <span className="dot" />
              <span className="dot" />
              {t("Working on it…")}
            </div>
          )}
          {c?.status === "failed" && c.error && <div className="banner banner-danger">{c.error}</div>}
          <div ref={end} />
        </div>
      )}

      {(record || uploads.length > 0 || uploading > 0) && (
        <div className="assistant-context">
          {record && (
            <button
              type="button"
              className="chip"
              onClick={() =>
                setDraft(
                  (d) => d || t("Summarise this {what} for me.", { what: record.label ?? t("record") }),
                )
              }
              title={t("The assistant knows you are looking at this; click to ask about it")}
            >
              <Icon name="sparkle" size={12} />
              {t("Ask about")} {record.label ?? record.entityType}
            </button>
          )}
          {uploads.map((u) => (
            <span key={u.id} className="chip on assistant-upload">
              <Icon name="paperclip" size={12} />
              {u.name}
              <button
                type="button"
                className="icon-btn"
                aria-label={t("Remove {name}", { name: u.name })}
                onClick={() => setUploads((x) => x.filter((y) => y.id !== u.id))}
              >
                <Icon name="x" size={11} />
              </button>
            </span>
          ))}
          {uploading > 0 && <span className="muted sm">{t("Uploading…")}</span>}
        </div>
      )}
      <form
        className="assistant-input"
        onSubmit={(e) => {
          e.preventDefault();
          send(draft);
        }}
      >
        <input
          ref={picker}
          type="file"
          multiple
          accept={ACCEPT}
          hidden
          onChange={(e) => {
            if (e.target.files) attach(e.target.files);
            e.target.value = "";
          }}
        />
        <button
          type="button"
          className="btn assistant-attach btn-icon"
          onClick={() => picker.current?.click()}
          disabled={thinking || uploads.length >= 5}
          aria-label={t("Attach a file")}
          title={t(
            "Attach a PDF, a picture, a spreadsheet or a text file (up to 10 MB, 5 at a time). You can also drop them here.",
          )}
        >
          <Icon name="paperclip" size={15} />
        </button>
        <textarea
          ref={box}
          aria-label={t("Ask the assistant")}
          placeholder={
            confirms.length ? t("Or write something else instead…") : t("Ask or tell me what to do…")
          }
          value={draft}
          rows={2}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          disabled={thinking}
        />
        <button
          type="submit"
          className="btn btn-primary btn-icon"
          disabled={(!draft.trim() && !uploads.length) || busy || thinking || uploading > 0}
          aria-label={t("Send")}
        >
          <Icon name="send" size={15} />
        </button>
      </form>
    </div>
  );
}

/** A change the assistant wants to make: what it says it will do, and the request itself. */
function ConfirmCard({ step, onAnswer }: { step: Step; onAnswer: (approve: boolean) => Promise<void> }) {
  const [busy, setBusy] = useState<"yes" | "no" | null>(null);
  const toast = useToast();
  const i = step.input ?? {};
  const answer = async (approve: boolean) => {
    setBusy(approve ? "yes" : "no");
    try {
      await onAnswer(approve);
    } catch (e) {
      toast((e as Error).message, "error");
      setBusy(null);
    }
  };
  return (
    <fieldset className="assistant-confirm" aria-label={t("Change to confirm")}>
      <div className="assistant-confirm-head">
        <Icon name="alert" size={14} />
        <strong>{i.summary || t("Make this change?")}</strong>
      </div>
      <code className="assistant-confirm-req">
        {String(i.method ?? "").toUpperCase()} {i.path}
      </code>
      {i.body !== undefined && (
        <details>
          <summary>{t("What will be sent")}</summary>
          <pre>{JSON.stringify(i.body, null, 2)}</pre>
        </details>
      )}
      <p className="muted assistant-confirm-note">
        {t("Nothing changes until you confirm. It runs with your rights, like doing it on the page.")}
      </p>
      <div className="row" style={{ gap: 8 }}>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!!busy}
          onClick={() => answer(true)}
        >
          {busy === "yes" ? t("Doing it…") : t("Confirm")}
        </button>
        <button type="button" className="btn btn-sm" disabled={!!busy} onClick={() => answer(false)}>
          {t("Cancel")}
        </button>
      </div>
    </fieldset>
  );
}

/** Whose key the assistant uses for you, and your own key (ERP-130). */
function KeyPanel({ status }: { status: Status }) {
  const close = useContext(CloseAssistant);
  const qc = useQueryClient();
  const toast = useToast();
  const mine = useQuery({ queryKey: ["assistant-my-key"], queryFn: () => api<MyKey>("/assistant/my-key") });
  const usage = useQuery({
    queryKey: ["assistant-my-usage"],
    queryFn: () => api<{ used: number; personal: number; limit: number | null }>("/assistant/my-usage"),
  });
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const m = mine.data;
  const change = async (fn: () => Promise<MyKey>, done: string) => {
    setBusy(true);
    try {
      const r = await fn();
      qc.setQueryData(["assistant-my-key"], r);
      await qc.invalidateQueries({ queryKey: ["assistant-status"] });
      toast(r.warning ?? done);
      setKey("");
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(false);
    }
  };
  const source = m?.source ?? status.source;
  return (
    <div className="assistant-keys stack">
      <p>
        {source === "personal"
          ? `${t("You are using your own key")} ${m?.keyHint ?? ""}.`
          : source === "company"
            ? t("You are using your company's key.")
            : source === "server"
              ? t("You are using the installation's key.")
              : t("There is no key for you yet.")}
      </p>
      {usage.data && (usage.data.used > 0 || usage.data.personal > 0 || usage.data.limit) && (
        <p className="muted sm">
          {t("This month")}: {usage.data.used.toLocaleString()} {t("tokens")}
          {usage.data.limit ? ` ${t("of")} ${usage.data.limit.toLocaleString()}` : ""}
          {usage.data.personal > 0 && ` · ${t("on your own key")}: ${usage.data.personal.toLocaleString()}`}
        </p>
      )}
      {m && !m.allowed ? (
        <p className="muted sm">
          {t("Your company uses its own key for everyone; personal keys are switched off.")}
        </p>
      ) : (
        m && (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              change(
                () => api<MyKey>("/assistant/my-key", { method: "PUT", body: { apiKey: key } }),
                t("Your key is saved"),
              );
            }}
          >
            <Field
              label={m.hasKey ? t("Replace your key") : t("Use your own Anthropic API key")}
              tip={t(
                "Your own key comes before the company's, and what you ask is billed to your Anthropic account. It is checked with Anthropic, stored encrypted and never shown again.",
              )}
            >
              <input
                type="password"
                autoComplete="off"
                placeholder="sk-ant-…"
                value={key}
                onChange={(e) => setKey(e.target.value)}
              />
            </Field>
            <div className="row" style={{ gap: 8 }}>
              <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !key.trim()}>
                {busy ? t("Checking…") : t("Save key")}
              </button>
              {m.hasKey && (
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={busy}
                  onClick={() =>
                    change(
                      () => api<MyKey>("/assistant/my-key", { method: "DELETE" }),
                      t("Your key is removed"),
                    )
                  }
                >
                  {t("Remove my key")}
                </button>
              )}
            </div>
          </form>
        )
      )}
      {status.canManage ? (
        <p className="muted sm">
          <Link className="assistant-link" to="/settings?tab=assistant" onClick={close}>
            {t("Company key, model and who can use the assistant")}
          </Link>
        </p>
      ) : (
        !source && (
          <p className="muted sm">
            {t("Or ask an admin to add the company's key under Settings → Assistant.")}
          </p>
        )
      )}
    </div>
  );
}
